import { useState } from "react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";

const STORIES = [
    {
        quote:
            "The expansion line was approved in days, not weeks. It felt less like borrowing and more like gaining a partner who already understood the business.",
        tag: "Enterprise Capital",
        kpi: "72 hrs",
        kpiLabel: "From brief to approval",
        accent: "gold",
    },
    {
        quote:
            "Refinancing was handled entirely online, yet a real advisor walked through every clause before we signed. The monthly saving spoke for itself.",
        tag: "Residence Mortgage",
        kpi: "AED 28K",
        kpiLabel: "Saved every month",
        accent: "forest",
    },
    {
        quote:
            "More than capital — the structure and introductions helped close the first enterprise contracts. A genuine operator's mindset, not a salesperson's pitch.",
        tag: "Venture Conviction",
        kpi: "21 days",
        kpiLabel: "Round closed in",
        accent: "coral",
    },
];

const ACCENT = {
    gold: { kpi: "text-gold", bar: "bg-gold", panel: "bg-gold", btn: "border-gold/40 hover:bg-gold hover:text-white hover:border-gold" },
    forest: { kpi: "text-forest", bar: "bg-forest", panel: "bg-forest", btn: "border-forest/40 hover:bg-forest hover:text-white hover:border-forest" },
    coral: { kpi: "text-coral", bar: "bg-coral", panel: "bg-coral", btn: "border-coral/40 hover:bg-coral hover:text-white hover:border-coral" },
};

export default function Testimonials() {
    const [i, setI] = useState(0);
    const s = STORIES[i];
    const ac = ACCENT[s.accent];

    const next = () => setI((i + 1) % STORIES.length);
    const prev = () => setI((i - 1 + STORIES.length) % STORIES.length);

    return (
        <section
            id="stories"
            data-testid="testimonials-section"
            className="relative py-20 md:py-28 bg-cream-warm"
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                <div className="grid md:grid-cols-12 gap-8 mb-12">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-8 h-px bg-gold" />
                            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                                Client Stories
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1] tracking-tight font-medium text-ink">
                            Numbers grow when<br />
                            <em className="italic text-gold font-normal">trust</em> arrives first.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9 flex items-end">
                        <p className="text-ink-muted font-light leading-relaxed text-lg">
                            4.9/5 across 8,200+ verified mandates — anonymised outcomes from a decade of private relationships.
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-12 gap-5">
                    {/* Branded KPI panel — no names, no faces */}
                    <div className={`md:col-span-4 relative aspect-[4/5] overflow-hidden ${ac.panel} text-white p-7 flex flex-col justify-between transition-colors duration-500`}>
                        {/* subtle texture */}
                        <div className="absolute inset-0 opacity-[0.12] pointer-events-none"
                            style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "16px 16px" }} />
                        <div className="relative flex items-center justify-between">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/90 border border-white/30 px-2 py-1">
                                {s.tag}
                            </div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
                                0{i + 1} / 0{STORIES.length}
                            </div>
                        </div>

                        <div className="relative">
                            <div className="font-serif text-6xl md:text-7xl leading-none drop-shadow-sm">
                                {s.kpi}
                            </div>
                            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/85 mt-3">
                                {s.kpiLabel}
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-8 bg-white border border-cream-deep p-7 md:p-10 flex flex-col justify-between">
                        <div className="flex items-center justify-between">
                            <Quote size={40} className={`${ac.kpi} opacity-50`} strokeWidth={1} />
                            <div className="flex gap-0.5">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <Star key={n} size={14} className="text-gold fill-gold" />
                                ))}
                            </div>
                        </div>
                        <blockquote
                            data-testid="testimonial-quote"
                            className="font-serif text-2xl md:text-3xl lg:text-4xl leading-[1.25] tracking-tight text-ink mt-5"
                        >
                            “{s.quote}”
                        </blockquote>

                        <div className="mt-8">
                            <div className="border-t border-cream-deep pt-5 flex items-center justify-between flex-wrap gap-3">
                                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink-muted">
                                    Verified Client · <span className={ac.kpi}>{s.tag}</span>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={prev}
                                        data-testid="testimonial-prev"
                                        className={`w-11 h-11 border text-ink-muted flex items-center justify-center transition-colors ${ac.btn}`}
                                        aria-label="Previous"
                                    >
                                        <ChevronLeft size={15} />
                                    </button>
                                    <button
                                        onClick={next}
                                        data-testid="testimonial-next"
                                        className={`w-11 h-11 border text-ink-muted flex items-center justify-center transition-colors ${ac.btn}`}
                                        aria-label="Next"
                                    >
                                        <ChevronRight size={15} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
